import { ChevronDown } from "lucide-react";
import { useCallback, useEffect, useId, useState } from "react";
import { Link, useLocation } from "react-router";

import { useAuth } from "@/app/providers/AuthProvider";
import { ROUTES } from "@/app/routes";
import LogoIcon from "@/components/common/LogoIcon";
import { isEntryActive, isRouteActive, MAIN_NAV, type NavEntry } from "@/components/layout/navigation";
import LogoutDialog from "@/features/auth/components/LogoutDialog";
import { useCurrentOrganization } from "@/features/organization/hooks/useOrganization";
import { cn } from "@/lib/utils";

export default function Sidebar({ className }: { className?: string }) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { organization } = useCurrentOrganization();
  const [confirmingLogout, setConfirmingLogout] = useState(false);

  const handleLogout = useCallback(async () => {
    setConfirmingLogout(false);
    await logout();
  }, [logout]);

  return (
    <>
      {confirmingLogout && <LogoutDialog open onLogout={handleLogout} onCancel={() => setConfirmingLogout(false)} />}

      <aside
        className={cn(
          "hidden md:flex w-64 shrink-0 flex-col justify-between overflow-hidden",
          "border-r border-border bg-card px-3 py-5",
          className,
        )}
      >
        {/* Scrolls on its own so the footer stays reachable when Stocks expands. */}
        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto">
          <BrandHeader />

          <nav aria-label="Parcours de production" className="flex flex-col gap-1">
            {MAIN_NAV.map((entry) => (
              <NavGroup key={entry.to} entry={entry} pathname={location.pathname} />
            ))}
          </nav>
        </div>

        <div className="flex shrink-0 flex-col gap-2 pt-3">
          <Link
            to={ROUTES.profile}
            aria-current={location.pathname === ROUTES.profile ? "page" : undefined}
            className="flex items-center gap-3 rounded-xl border border-border bg-muted/60 px-3 py-2.5 transition hover:border-foreground/20 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span
              aria-hidden
              className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-brand text-sm font-semibold text-brand-black"
            >
              {(organization?.name ?? "N").slice(0, 1).toUpperCase()}
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold">{organization?.name ?? "Nafa Pro"}</p>
              <p className="truncate text-xs text-muted-foreground">{user?.username}</p>
            </div>
          </Link>

          <button
            type="button"
            onClick={() => setConfirmingLogout(true)}
            className="h-11 rounded-lg px-4 text-left text-sm font-medium text-muted-foreground transition hover:bg-muted hover:text-foreground"
          >
            Se déconnecter
          </button>
        </div>
      </aside>
    </>
  );
}

function BrandHeader() {
  return (
    <div className="flex items-center gap-2 px-2">
      <LogoIcon size={34} primaryColor="var(--nafa-black)" secondaryColor="var(--nafa-yellow)" />
      <div className="leading-none">
        <p className="text-xl font-bold tracking-tight">nafa</p>
        <p className="text-[11px] font-bold tracking-widest text-muted-foreground">PRO</p>
      </div>
    </div>
  );
}

function NavGroup({ entry, pathname }: { entry: NavEntry; pathname: string }) {
  const active = isEntryActive(pathname, entry);
  const hasChildren = (entry.children?.length ?? 0) > 0;
  const parentActive = hasChildren ? active : isRouteActive(pathname, entry.to);
  const [expanded, setExpanded] = useState(active);
  const collapsedActive = active && !expanded;
  const childrenId = useId();

  useEffect(() => {
    if (active) setExpanded(true);
  }, [active, pathname]);

  return (
    <div className="flex flex-col">
      {hasChildren ? (
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={childrenId}
          aria-current={collapsedActive ? "location" : undefined}
          onClick={() => setExpanded((value) => !value)}
          className={cn(
            "relative flex h-12 w-full items-center gap-3 rounded-lg px-4 text-left text-[15px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            collapsedActive
              ? "bg-foreground font-semibold text-background"
              : "text-foreground/80 hover:bg-muted",
          )}
        >
          {collapsedActive && (
            <span aria-hidden="true" className="absolute left-0 h-7 w-1 rounded-full bg-brand" />
          )}
          {entry.icon}
          <span className="flex-1 truncate">{entry.label}</span>
          <ChevronDown
            size={16}
            aria-hidden="true"
            className={cn(
              "shrink-0 transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none",
              !expanded && "-rotate-90",
            )}
          />
        </button>
      ) : (
        <NavLink to={entry.to} label={entry.label} icon={entry.icon} active={parentActive} />
      )}

      {hasChildren && (
        <div
          id={childrenId}
          inert={!expanded}
          aria-hidden={!expanded}
          className={cn(
            "grid transition-[grid-template-rows,opacity] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none",
            expanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
          )}
        >
          <div className="min-h-0 overflow-hidden">
            <div className="flex flex-col gap-1 pt-1">
              {entry.children?.map((child) => (
                <NavLink
                  key={child.to}
                  to={child.to}
                  label={child.label}
                  active={expanded && isRouteActive(pathname, child.to)}
                  nested
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function NavLink({
  to,
  label,
  icon,
  active,
  nested = false,
  emphasised = false,
}: {
  to: string;
  label: string;
  icon?: React.ReactNode;
  active: boolean;
  nested?: boolean;
  emphasised?: boolean;
}) {
  return (
    <div className="flex items-center">
      {/* The yellow marker replaces part of the padding so labels stay aligned. */}
      <span
        aria-hidden
        className={cn(
          "mr-0 w-1 shrink-0 rounded-full bg-brand transition-all",
          active ? (nested ? "h-5" : "h-7") : "h-0 w-0",
        )}
      />
      <Link
        to={to}
        aria-current={active ? "page" : undefined}
        className={cn(
          "flex flex-1 items-center gap-3 rounded-lg transition",
          nested ? "h-10 text-sm" : "h-12 text-[15px]",
          active ? "bg-foreground font-semibold text-background" : "text-foreground/80 hover:bg-muted",
          !active && nested && "text-muted-foreground",
          emphasised && !active && "font-medium text-foreground",
        )}
        style={{ paddingLeft: (nested ? 40 : 16) - (active ? 4 : 0), paddingRight: 12 }}
      >
        {icon}
        <span className="truncate">{label}</span>
      </Link>
    </div>
  );
}
